import axios from "axios";
import { findRoute, mustStayLocal, runLocal, cacheKey } from "@/lib/offline/local";
import { storage } from "@/lib/offline/storage";
import { setState, isNetworkError } from "@/lib/offline/state";

export const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

// Platform admin "support" mode: on /admin?workplace=<id>, admin routes target that workplace (server only honours it for platform admins).
api.interceptors.request.use((cfg) => {
  const acting = window.location.pathname === "/admin" ? new URLSearchParams(window.location.search).get("workplace") : null;
  if (acting && (cfg.url || "").startsWith("/admin/")) cfg.headers["X-Workplace-Id"] = acting;
  return cfg;
});

const CACHEABLE = /^\/(auth\/me|referentials|activities|teams\/mine|teams\/[^/]+\/candidates|equipment\/[^/]+|take5s\/[0-9a-f-]{36}|inspections\/[0-9a-f-]{36})$/;
const netAdapter = axios.getAdapter(axios.defaults.adapter);

// Offline-aware transport: network first, local emulation when offline or while an entity has unsent changes.
api.defaults.adapter = async (config) => {
  const route = findRoute(config);
  if (route && (!navigator.onLine || await mustStayLocal(config))) return { ...(await runLocal(route, config)), headers: {}, config, statusText: "OK" };
  try {
    const res = await netAdapter(config);
    const url = (config.url || "").split("?")[0];
    const p = config.params || {};
    const plainList = url === "/equipment" && !p.q && !p.site_id && !p.category && !p.status;
    if ((config.method || "get") === "get" && config.responseType !== "blob" && (CACHEABLE.test(url) || plainList)) {
      // The adapter runs before axios' JSON transform: store the parsed object, not the raw string.
      let body = res.data;
      if (typeof body === "string") { try { body = JSON.parse(body); } catch { body = undefined; } }
      if (body !== undefined) storage.put("cache", cacheKey(url, p), body).catch(() => {});
    }
    return res;
  } catch (e) {
    if (route && isNetworkError(e)) {
      setState({ online: false });
      return { ...(await runLocal(route, config)), headers: {}, config, statusText: "OK" };
    }
    throw e;
  }
};

let refreshing = null;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config;
    const url = cfg?.url || "";
    if (error.response?.status === 401 && !cfg) {
      window.dispatchEvent(new Event("auth:expired"));
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && !cfg._retry && !url.startsWith("/auth/")) {
      cfg._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(cfg);
      } catch (e) {
        refreshing = null;
        window.dispatchEvent(new Event("auth:expired"));
      }
    }
    return Promise.reject(error);
  }
);

export function errMessage(e) {
  if (!e?.response) return "Erreur réseau : vérifiez votre connexion et réessayez.";
  const d = e.response.data?.detail;
  if (d == null) return "Une erreur est survenue. Réessayez.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "Donnée invalide").join(" ");
  if (d.message) return d.message;
  return "Une erreur est survenue.";
}

export function errFields(e) {
  const d = e?.response?.data?.detail;
  return d && typeof d === "object" && !Array.isArray(d) && d.errors ? d.errors : {};
}

export function focusFirstError(errors) {
  const key = Object.keys(errors)[0];
  if (!key) return;
  setTimeout(() => {
    const el = document.querySelector(`[data-field="${key}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 50);
}
